import type {ReactNode, SyntheticEvent} from 'react'
import noop from 'lodash/noop'
import uniqueId from 'lodash/uniqueId'
import {exhaustive} from '@freckle/exhaustive'
import {
  addMaybeClassName,
  addMaybeClassNames
} from '@freckle/educator-entities/ts/common/helpers/classnames'
import type {ScreenReaderTextT as ChevronScreenReaderTextT} from '@freckle/educator-materials/ts/components/expand-chevron-button'
import {ExpandChevronButton} from '@freckle/educator-materials/ts/components/expand-chevron-button'
import type {ChevronDirectionT} from '@freckle/educator-materials/ts/components/expand-chevron-button'
import {Toggle} from '@freckle/educator-materials/ts/components/toggle'
import type {AppThemeT} from '@freckle/educator-materials/ts/helpers/themes'
import {
  primaryAndExpandable,
  primaryContainerEnabled,
  primaryContainerDisabled,
  primaryContainerRight,
  primaryContainerLeft,
  chevronWrapper,
  classroomPrimaryAndExpandable,
  classroomExpandedContainer,
  classroomCollapsedContainer,
  classroomExpandableComponentContainer,
  renaissancePrimaryAndExpandable,
  renaissanceExpandableComponentContainer,
  renaissanceChevronButton,
  basicContainer,
  basicExpandableComponentContainer
} from './expandable-container.module.scss'

type ChevronPositionT = 'left' | 'right'

type Props = {
  children: (open: boolean) => ReactNode
  expandableComponent: ReactNode
  theme: AppThemeT
  className?: string
  chevronAddClass?: string
  chevronWrapperAddClass?: string
  expandableComponentAddClass?: string
  primaryContainerAddClass?: string
  primaryContainerExpandedAddClass?: string
  initOpen?: boolean | null
  chevronPosition?: ChevronPositionT
  iconExpand?: ChevronDirectionT
  iconCollapse?: ChevronDirectionT
  disabled?: boolean
  onToggle?: (isExpanded: boolean) => void
  expanded?: boolean // This is the "controlled" version of initOpen
  dataTest?: string
  screenReaderText?: {
    chevron: ChevronScreenReaderTextT
  }
}

function ExpandableContainer(props: Props) {
  const {
    children,
    expandableComponent,
    theme,
    className: containerclassName,
    chevronAddClass,
    chevronWrapperAddClass,
    initOpen = false,
    chevronPosition = 'right',
    onToggle: onToggleProp,
    expanded,
    dataTest,
    expandableComponentAddClass,
    primaryContainerAddClass,
    primaryContainerExpandedAddClass,
    screenReaderText,
    ...chevronIcon
  } = props

  const {containerClass, expandableComponentContainer, getExpandedContainerClass, getButtonClass} =
    getTheme(theme)

  const themedContainerClass = addMaybeClassName(containerClass, containerclassName)
  const defaultIconExpand: ChevronDirectionT = theme === 'renaissance' ? 'up' : 'down'
  const defaultIconCollapse: ChevronDirectionT = theme === 'renaissance' ? 'down' : 'right'

  const dataTestObj = dataTest !== undefined ? {'data-test': dataTest} : {}

  const renderChildrenWithChevron = (
    onToggle: () => void,
    open: boolean,
    expandableId: string
  ): ReactNode => {
    const [onClick, primaryContainerBase] =
      props.disabled === true
        ? [(_isExpanded: boolean) => {}, primaryContainerDisabled]
        : [getOnToggle(onToggleProp, onToggle), primaryContainerEnabled]

    // This is necessary to avoid a duplicate event with it's container
    const chevronOnClick = (e: SyntheticEvent<EventTarget, Event>) => {
      e.stopPropagation()
      onClick(!open) // Invert state because this is called prior to expanding the component
    }

    const chevronClass = [getButtonClass(open), chevronAddClass].filter(Boolean).join(' ')

    switch (chevronPosition) {
      case 'right':
        return (
          <div
            className={addMaybeClassNames(primaryContainerRight, [
              primaryContainerBase,
              primaryContainerAddClass,
              open ? primaryContainerExpandedAddClass : null,
              getExpandedContainerClass(open)
            ])}
            onClick={() => onClick(!open)}
          >
            {children(open)}
            <div className={addMaybeClassName(chevronWrapper, chevronWrapperAddClass)}>
              <ExpandChevronButton
                expand={open}
                onClick={chevronOnClick}
                className={chevronClass}
                iconExpand={defaultIconExpand}
                iconCollapse={defaultIconCollapse}
                screenReaderText={screenReaderText?.chevron}
                ariaControls={expandableId}
                {...chevronIcon}
              />
            </div>
          </div>
        )
      case 'left':
        return (
          <div
            className={addMaybeClassNames(primaryContainerLeft, [
              primaryContainerBase,
              primaryContainerAddClass,
              open ? primaryContainerExpandedAddClass : null,
              getExpandedContainerClass(open)
            ])}
            onClick={() => onClick(!open)}
          >
            <div className={addMaybeClassName(chevronWrapper, chevronWrapperAddClass)}>
              <ExpandChevronButton
                expand={open}
                onClick={chevronOnClick}
                className={chevronClass}
                iconExpand={defaultIconExpand}
                iconCollapse={defaultIconCollapse}
                screenReaderText={screenReaderText?.chevron}
                ariaControls={expandableId}
                {...chevronIcon}
              />
            </div>
            {children(open)}
          </div>
        )
      default:
        exhaustive(chevronPosition, `Invalid chevron position`)
    }
  }

  // Use a key in order to tell Toggle to rerender with a new expanded prop when it changes
  const key = expanded === true ? 0 : 1
  const expandableWrapperId = uniqueId('expandable-component-container-')

  return (
    <Toggle initOpen={initOpen === true || expanded === true} key={key}>
      {(onToggle, open) => (
        <div className={themedContainerClass} {...dataTestObj}>
          {renderChildrenWithChevron(onToggle, open, expandableWrapperId)}
          {open ? (
            <div
              className={addMaybeClassName(
                expandableComponentContainer,
                expandableComponentAddClass
              )}
              id={expandableWrapperId}
            >
              {expandableComponent}
            </div>
          ) : null}
        </div>
      )}
    </Toggle>
  )
}

type ExpandableContainerThemeT = {
  containerClass: string
  expandableComponentContainer: string
  getExpandedContainerClass: (open: boolean) => string
  getButtonClass: (open: boolean) => string
}

function getTheme(theme: AppThemeT): ExpandableContainerThemeT {
  switch (theme) {
    case 'classroom':
      return {
        containerClass: classroomPrimaryAndExpandable,
        expandableComponentContainer: classroomExpandableComponentContainer,
        getExpandedContainerClass: open =>
          open ? classroomExpandedContainer : classroomCollapsedContainer,
        getButtonClass: _open => ''
      }
    case 'basic':
      return {
        containerClass: primaryAndExpandable,
        expandableComponentContainer: basicExpandableComponentContainer,
        getExpandedContainerClass: _open => basicContainer,
        getButtonClass: _open => ''
      }
    case 'renaissance':
      return {
        containerClass: renaissancePrimaryAndExpandable,
        expandableComponentContainer: renaissanceExpandableComponentContainer,
        getExpandedContainerClass: _open => '',
        getButtonClass: _open => renaissanceChevronButton
      }
    default:
      throw new Error(`Unsupported theme: ${theme}`)
  }
}

function getOnToggle(
  mOnToggleProp: ((isExpanded: boolean) => void) | undefined | null,
  internalOnToggle: () => void
): (isExpanded: boolean) => void {
  const onToggleProp = mOnToggleProp ?? noop
  return (isExpanded: boolean) => {
    internalOnToggle()
    onToggleProp(isExpanded)
  }
}

export {ExpandableContainer}
